import { isAxiosError } from "@/api/api";
import { Result } from "@/common/common";
import { BooleanFieldModel } from "@/common/commonForms";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { TransactionDateCardModel } from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { IssuedByModel } from "@/shareholderRegister/transactions/issue/issueTransactionDomain";
import { transactionValidationErrorMessage } from "@/stores/TransactionStore";
import { CompanyInfo } from "@/types/CompanyInfo";
import { Transaction } from "@/types/Transactions";
import { adminShareholdersTransactions } from "@/utils/navigation";
import Big from "big.js";
import { isObject } from "lodash";
import {
  commandFailureMessage,
  ExerciseOptionsCommand,
  ExerciseShareIssue,
} from "./instrumentsApiClient";

export type ExerciseIssueRow = {
  key: string;
  holderName: string;
  program: TMessage;
  receiverName: string;
  shareTypeId?: number;
  count: number;
  strikePrice: number;
  isEmployee: boolean;
};

export type ExerciseIssueDraft = {
  orgNo: string;
  command: ExerciseOptionsCommand;
  rows: ExerciseIssueRow[];
  files: File[];
  submitResult?: Result<TMessage, TMessage>;
};

export const exerciseIssueUrl = (orgNo: string): string =>
  `${adminShareholdersTransactions(orgNo)}/new/issue?exercise=true`;

export type ShareCapitalFigures = Pick<
  CompanyInfo,
  | "totalShares"
  | "shareNominalValue"
  | "shareCapital"
  | "totalPaidInShareCapital"
  | "totalPaidInSharePremium"
  | "numberOfSharesPerShareholder"
>;

export type ShareIssueSummaryRow = {
  label: TMessage;
  before: TMessage | TMessageParam;
  after: TMessage | TMessageParam;
};

export type IssueReceiver =
  | { type: "SHAREHOLDER"; shareholderId: number }
  | { type: "NOT_SHAREHOLDER"; key: string };

export type IssuedShares = {
  count: number;
  pricePerShare: number;
  receiver: IssueReceiver;
};

const summaryAmount = (value: number | undefined, currency: string): TMessage | TMessageParam =>
  value === undefined ? { translated: "–" } : { type: "amount", value, currency };

const newShareholderKey = (
  receiver: IssueReceiver,
  shareholdersWithShares: Set<number>,
): string | undefined => {
  switch (receiver.type) {
    case "SHAREHOLDER":
      return shareholdersWithShares.has(receiver.shareholderId)
        ? undefined
        : `shareholder:${receiver.shareholderId}`;
    case "NOT_SHAREHOLDER":
      return receiver.key;
  }
};

const plusAll = (issued: IssuedShares[], perShare: (issue: IssuedShares) => Big): Big =>
  issued.reduce((sum, issue) => sum.plus(perShare(issue).times(issue.count)), Big(0));

export const shareIssueSummary = (
  issued: IssuedShares[],
  figures: ShareCapitalFigures,
  currency: string,
): ShareIssueSummaryRow[] => {
  const counted = issued.filter((issue) => issue.count > 0);
  const nominalValue = figures.shareNominalValue;
  const shares = counted.reduce((sum, issue) => sum + issue.count, 0);
  const paidIn = plusAll(counted, (issue) => Big(issue.pricePerShare));
  const premium = plusAll(counted, (issue) => Big(issue.pricePerShare).minus(nominalValue));
  const paidInCapital =
    figures.totalPaidInShareCapital === undefined || figures.totalPaidInSharePremium === undefined
      ? undefined
      : figures.totalPaidInShareCapital + figures.totalPaidInSharePremium;
  const shareholdersWithShares = new Set(
    Object.entries(figures.numberOfSharesPerShareholder)
      .filter(([, count]) => count > 0)
      .map(([shareholderId]) => Number(shareholderId)),
  );
  const newShareholders = new Set(
    counted.flatMap((issue) => newShareholderKey(issue.receiver, shareholdersWithShares) ?? []),
  );
  return [
    {
      label: { key: "decisions.increaseShareCapital.summary.numberOfShares" },
      before: { type: "number", value: figures.totalShares },
      after: { type: "number", value: figures.totalShares + shares },
    },
    {
      label: { key: "decisions.increaseShareCapital.summary.totalShareCapital" },
      before: summaryAmount(figures.shareCapital, currency),
      after: summaryAmount(
        Big(figures.shareCapital).plus(Big(nominalValue).times(shares)).toNumber(),
        currency,
      ),
    },
    {
      label: { key: "decisions.increaseShareCapital.summary.paid-in-premium" },
      before: summaryAmount(figures.totalPaidInSharePremium, currency),
      after: summaryAmount(
        figures.totalPaidInSharePremium === undefined
          ? undefined
          : premium.plus(figures.totalPaidInSharePremium).toNumber(),
        currency,
      ),
    },
    {
      label: { key: "decisions.increaseShareCapital.summary.paid-in-capital" },
      before: summaryAmount(paidInCapital, currency),
      after: summaryAmount(
        paidInCapital === undefined ? undefined : paidIn.plus(paidInCapital).toNumber(),
        currency,
      ),
    },
    {
      label: { key: "decisions.increaseShareCapital.summary.numberOfShareholders" },
      before: { type: "number", value: shareholdersWithShares.size },
      after: { type: "number", value: shareholdersWithShares.size + newShareholders.size },
    },
  ];
};

export type ExerciseIssueForm = {
  isRegistered: BooleanFieldModel;
  registrationDate: TransactionDateCardModel;
  shareholderRightsDate: TransactionDateCardModel;
  issuedBy: IssuedByModel;
};

export const newExerciseIssueForm = (data: {
  transactionLog: Transaction[];
}): ExerciseIssueForm => ({
  isRegistered: new BooleanFieldModel({ value: true }),
  registrationDate: new TransactionDateCardModel({ data }),
  shareholderRightsDate: new TransactionDateCardModel({ data }),
  issuedBy: new IssuedByModel(() => undefined),
});

export const validateExerciseIssueForm = (form: ExerciseIssueForm): boolean => {
  const issuedByValid = form.issuedBy.validate();
  if (form.isRegistered.value) {
    form.registrationDate.validate();
    return form.registrationDate.isValid && issuedByValid;
  }
  const hasDecisionDate = form.issuedBy.decisionDate.validatedValue !== undefined;
  if (hasDecisionDate) {
    form.shareholderRightsDate.clearErrors();
  } else {
    form.shareholderRightsDate.validate();
  }
  return (form.shareholderRightsDate.isValid || hasDecisionDate) && issuedByValid;
};

export const exerciseShareIssue = (form: ExerciseIssueForm): ExerciseShareIssue => ({
  registrationDate: form.isRegistered.value ? form.registrationDate.combinedDateAndTime : undefined,
  shareholderRightsDate: form.isRegistered.value
    ? undefined
    : form.shareholderRightsDate.combinedDateAndTime,
  ...form.issuedBy.prepareData(),
});

export const exerciseIssueTotals = (draft: ExerciseIssueDraft): { count: number; amount: number } =>
  draft.rows.reduce(
    (acc, row) => ({
      count: acc.count + row.count,
      amount: acc.amount + row.count * row.strikePrice,
    }),
    { count: 0, amount: 0 },
  );

export const exerciseIssueFailureMessage = (err: unknown): TMessage => {
  const data: unknown = isAxiosError(err) ? err.response?.data : undefined;
  if (isObject(data) && "validationError" in data && typeof data.validationError === "string") {
    return transactionValidationErrorMessage(data.validationError, { key: "general-error" });
  }
  return commandFailureMessage(err);
};
